import { c as _c } from "react/compiler-runtime";
import { useBuilder } from '../../store/provider';
import { type SuggestionSection, useSuggestionSections } from '../../store/suggestion-sections';
import type { Suggestion } from '../../store/types';
export type PendingChanges = {
  sections: SuggestionSection[];
  count: number;
  acceptableIds: string[];
  dismissibleIds: string[];
  // add_node suggestion id → suggestions targeting the node it creates. They have no section of
  // their own (their node isn't on the head graph yet) and render nested under the add_node row.
  chainedByParentId: Map<string, Suggestion[]>;
  // Pending suggestions with no section row and no add_node anchor — the target was renamed
  // (field/rules keys follow the name) or removed. Artifact keys don't follow the label.
  orphans: Suggestion[];
};
const targetNodeId = (s: Suggestion): string | undefined => {
  const op = s.operation;
  if (op.kind === 'add_node') return op.parentNodeId ?? undefined;
  if (op.kind === 'update_node') return op.node.id;
  if ('nodeId' in op) return op.nodeId;
  return undefined;
};

// Accept/Dismiss act on every pending suggestion, not just the rendered rows. Stale suggestions
// are dropped from Accept (the backend applies the batch atomically, so one inapplicable
// suggestion would roll back the rest) but kept in Dismiss. Chained children of an unaccepted
// add_node are the exception: they can't apply alone but the backend applies the batch in request
// order, so their parent runs first and unblocks them.
export const derivePendingChanges = (allPending: Suggestion[], sections: SuggestionSection[]): PendingChanges => {
  const rows = sections.flatMap(s => s.items);
  const renderedIds = new Set(rows.map(s => s.id));
  const addNodeByNewId = new Map<string, Suggestion>();
  for (const s of allPending) {
    if (s.operation.kind === 'add_node') addNodeByNewId.set(s.operation.node.id, s);
  }

  // Walk up the add_node chain to the one that renders a row (its parent is the head or an existing node).
  const anchorFor = (s: Suggestion, seen: Set<string>): Suggestion | undefined => {
    if (seen.has(s.id)) return undefined;
    seen.add(s.id);
    const parentNodeId = targetNodeId(s);
    const parentAdd = parentNodeId ? addNodeByNewId.get(parentNodeId) : undefined;
    if (!parentAdd) return undefined;
    return renderedIds.has(parentAdd.id) ? parentAdd : anchorFor(parentAdd, seen);
  };
  const chainedByParentId = new Map<string, Suggestion[]>();
  const chainedIds = new Set<string>();
  const orphans: Suggestion[] = [];
  for (const s of allPending) {
    if (renderedIds.has(s.id)) continue;
    const anchor = anchorFor(s, new Set());
    if (!anchor) {
      orphans.push(s);
      continue;
    }
    chainedIds.add(s.id);
    const list = chainedByParentId.get(anchor.id);
    if (list) list.push(s);else chainedByParentId.set(anchor.id, [s]);
  }
  return {
    sections,
    count: allPending.length,
    acceptableIds: allPending.filter(s => s.canApply || chainedIds.has(s.id)).map(s => s.id),
    dismissibleIds: allPending.map(s => s.id),
    chainedByParentId,
    orphans
  };
};
export const usePendingChanges = () => {
  const $ = _c(3);
  const allPending = useBuilder().state.bundle.suggestions;
  const sections = useSuggestionSections();
  let t0;
  if ($[0] !== allPending || $[1] !== sections) {
    t0 = derivePendingChanges(allPending, sections);
    $[0] = allPending;
    $[1] = sections;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};